import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, ShieldCheck, Download, Trash2, Send, CheckCircle2, XCircle } from "lucide-react";
import { useDebounce } from "use-debounce";
import Pagination from "../../../../components/common/Pagination";
import { getCertificateExportLogs } from "../../../../api/elearning/certificates/certificates";

const OUTCOME_META = {
  success: {
    label: "Thành công",
    className: "bg-emerald-50 text-emerald-700",
  },
  rate_limited: {
    label: "Bị chặn 5 phút",
    className: "bg-amber-50 text-amber-800",
  },
  failed: {
    label: "Thất bại",
    className: "bg-red-50 text-red-700",
  },
};

const EVENT_META = {
  file_download: {
    label: "Tải file",
    className: "bg-slate-100 text-slate-700",
    Icon: Download,
  },
  request_deleted: {
    label: "Xóa yêu cầu",
    className: "bg-red-50 text-red-800",
    Icon: Trash2,
  },
  request_submitted: {
    label: "Gửi yêu cầu",
    className: "bg-blue-50 text-blue-800",
    Icon: Send,
  },
  hq_approved: {
    label: "Duyệt cấp 1",
    className: "bg-emerald-50 text-emerald-800",
    Icon: CheckCircle2,
  },
  mgmt_approved: {
    label: "Duyệt cấp 2",
    className: "bg-emerald-50 text-emerald-900",
    Icon: CheckCircle2,
  },
  rejected: {
    label: "Từ chối",
    className: "bg-amber-50 text-amber-900",
    Icon: XCircle,
  },
};

const REQUEST_STATUS_LABEL = {
  pending_hq: "Chờ duyệt chuyên môn",
  pending_mgmt: "Chờ duyệt lãnh đạo",
  completed: "Hoàn thành",
  rejected: "Từ chối",
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const digits = size >= 100 || unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(digits)} ${units[unitIndex]}`;
};

const CertificateZipHistoryTab = () => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, 400);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCertificateExportLogs({
        page,
        size: pageSize,
        keyword: debouncedSearch.trim() || undefined,
      });
      setRecords(Array.isArray(data?.records) ? data.records : []);
      setTotal(Number(data?.total || 0));
    } catch {
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => (loading ? [] : records), [loading, records]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-700">
                <ShieldCheck size={18} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-black text-gray-800 uppercase tracking-wide">
                  Nhật ký phê duyệt / tải file
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Theo dõi tiến độ duyệt yêu cầu, lý do xóa, lịch sử tải ZIP/PDF và các sự kiện liên quan.
                </p>
              </div>
            </div>

            <div className="relative w-full group">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
                size={18}
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Tìm theo người thực hiện, lớp, chi nhánh, lý do xóa, mã yêu cầu..."
                className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => load()}
            className="h-11 w-11 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 shrink-0"
            title="Làm mới"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto relative min-h-[180px]">
          {loading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
              <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
            </div>
          )}

          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Thời gian</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Sự kiện</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Người thực hiện</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Yêu cầu / Lớp</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Chi tiết</th>
                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Thiết bị / IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row) => {
                const eventMeta = EVENT_META[row.event_type] || {
                  label: row.event_type || "Không rõ",
                  className: "bg-gray-100 text-gray-700",
                  Icon: ShieldCheck,
                };
                const EventIcon = eventMeta.Icon;
                const isDownload = row.event_type === "file_download";
                const outcomeMeta = isDownload
                  ? OUTCOME_META[row.status] || {
                      label: row.status || "Không rõ",
                      className: "bg-gray-100 text-gray-700",
                    }
                  : null;

                return (
                  <tr key={row.id} className="hover:bg-gray-50/50">
                    <td className="p-4 text-xs font-medium text-gray-600 whitespace-nowrap">
                      {(row.event_at || row.created_at)
                        ? new Date(row.event_at || row.created_at).toLocaleString("vi-VN")
                        : "—"}
                    </td>
                    <td className="p-4 min-w-[140px]">
                      <span
                        className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-black uppercase ${eventMeta.className}`}
                      >
                        <EventIcon size={11} />
                        {eventMeta.label}
                      </span>
                      {isDownload && row.format && (
                        <p className="mt-1 text-[10px] font-bold uppercase text-gray-500">{row.format}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="text-sm font-bold text-gray-800">{row.actor_name || row.user_name || "—"}</p>
                      <p className="text-[11px] text-gray-500">{row.actor_email || row.user_email || "—"}</p>
                    </td>
                    <td className="p-4 min-w-[220px]">
                      <p className="text-sm font-bold text-gray-800">{row.class_name || "—"}</p>
                      <p className="text-[11px] text-gray-500">{row.branch_name || "—"}</p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400 font-bold">
                        {row.request_id ? `Yêu cầu #${String(row.request_id).slice(0, 8)}` : "—"}
                      </p>
                      {row.request_status && (
                        <p className="mt-1 text-[10px] font-bold text-gray-500">
                          Trạng thái: {REQUEST_STATUS_LABEL[row.request_status] || row.request_status}
                        </p>
                      )}
                    </td>
                    <td className="p-4 min-w-[260px]">
                      {row.event_type === "request_deleted" && row.delete_reason && (
                        <p className="mb-2 text-xs leading-relaxed text-red-800 bg-red-50/60 rounded-lg px-2 py-1.5 border border-red-100">
                          <span className="font-black uppercase text-[10px] tracking-wide text-red-600 block mb-0.5">
                            Lý do xóa
                          </span>
                          {row.delete_reason}
                        </p>
                      )}
                      {isDownload && (
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="text-xs font-bold text-gray-700">
                            {row.certificate_count || 0} chứng nhận
                          </span>
                          <span className="text-[11px] text-gray-500">{formatBytes(row.transfer_bytes)}</span>
                          {outcomeMeta && (
                            <span
                              className={`inline-flex rounded-lg px-2 py-0.5 text-[10px] font-black uppercase ${outcomeMeta.className}`}
                            >
                              {outcomeMeta.label}
                            </span>
                          )}
                        </div>
                      )}
                      {!isDownload && row.event_type !== "request_deleted" && row.certificate_count > 0 && (
                        <p className="mb-1 text-[11px] font-bold text-gray-600">
                          {row.certificate_count} người trong yêu cầu
                        </p>
                      )}
                      {row.event_type === "request_deleted" && row.certificate_count > 0 && (
                        <p className="mb-1 text-[11px] font-bold text-gray-600">
                          Đã thu hồi {row.certificate_count} chứng nhận đã cấp
                        </p>
                      )}
                      <p className="text-xs leading-relaxed text-gray-600">
                        {row.detail_message || row.message || "—"}
                      </p>
                    </td>
                    <td className="p-4 min-w-[200px]">
                      <p className="text-xs font-bold text-gray-700">{row.client_ip || "—"}</p>
                      <p
                        className="mt-1 text-[11px] leading-relaxed text-gray-500 break-all"
                        title={row.user_agent || ""}
                      >
                        {row.user_agent || "—"}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!loading && rows.length === 0 && (
            <div className="p-12 text-center text-gray-400 text-sm font-bold">
              Chưa có nhật ký phù hợp.
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="p-4 border-t border-gray-50">
            <Pagination
              totalRecords={total}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificateZipHistoryTab;
